import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Page from '@components/Page/Page';
import {
    copyToClipboard,
    equalInLC,
    includesInLC,
    startsFromInLC,
    Tooltip,
} from "@datassential/platform-ui-lib";
import { toast } from 'react-toastify';
import { CSSTransition } from 'react-transition-group';
import SelectFilters from "@vms/SelectFilters";
import { ByoDataContextProvider, useByoDataContext } from "./ByoContext";
import SearchField from "@components/SearchField/SearchField";
import { useFilters } from "@vms/SelectFilters/SelectFiltersContext";
import ProgressPanel from '@components/ProgressPanel/ProgressPanel';
import FooterContent from '@pages/Byo/components/FooterContent/FooterContent';
import { usePairedFlavors } from './usePairedFlavors';
import { useByoTableData } from "@pages/Byo/useByoTableData";
import SlicesTabs from "@pages/Byo/components/SlicesTabs/SlicesTabs";
import ChartPanel from './components/ChartPanel/ChartPanel';
import AnalyticsTypeTabs from './components/AnalyticsTypeTabs/AnalyticsTypeTabs';
import TableSortingContext, { TableSortingContextType } from "@components/TableNew/TableSorting";
import { useByoColors } from "@pages/Byo/useByoColors";
import { getFormattedDate } from "@core/old_helpers";
import { formatCopyDataAsString, getCopyTableData } from '@components/TableNew/TableUtils';
import { TableColumn } from '@components/TableNew/TableColumns';
import { useByoTableConfig } from './components/ByoTable/ByoTableConfig';
import ByoTable from './components/ByoTable/ByoTable';
import { useByoChartData } from './useByoChartData';
import { Sort } from "@models";
import AiResultSection from '@vms/AiResult/AiResultSection';
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import { useAiSearchByoSettings } from "@pages/Byo/useAiSearchByoSettings";
import useQueryState from "@hooks/useQueryState";
import {
    BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME, BYO_SETTINGS_ID_QUERY_PARAM_NAME,
    BYO_WORDS_QUERY_PARAM_NAME
} from "@constants/general";
import {useInstantChartNameById} from "@apiHooks/useInstantChartNameById";
import {useByoSettingNameById} from "@apiHooks/useBySettingNameById";

const SUPPORT_CONTACT_INFO = 'Questions? Call us at (312) 655-0622 or email support@datassential.com';
const RESTAURANTS_LISTS_WITHOUT_CHART = [1394];

const ByoPageContent: FC = () => {
    const [byoSettingsIdString] = useQueryState(BYO_SETTINGS_ID_QUERY_PARAM_NAME);
    const [instantChartIdString] = useQueryState(BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME);

    const { data: byoSettingName } = useByoSettingNameById(byoSettingsIdString ?? null);
    const { data: instantChartName } = useInstantChartNameById(instantChartIdString ?? null);


    const {
        keywords,
        activeRestaurantListId,
    } = useFilters();
    const { data, addWordsFromQuery, activeAnalyticsType } = useByoDataContext();
    const { resetSearch } = useAiSearchSettingsContext();

    const hasCharts = !RESTAURANTS_LISTS_WITHOUT_CHART.includes(activeRestaurantListId);

    const [search, setSearch] = useState('');
    const [isSearchError, setIsSearchError] = useState(false);
    const [activeKeywordName, setActiveKeywordName] = useState<string | null>(null);
    const {
        slices,
        columns,
        data: tableData,
        consumerAccessRestricted,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useByoTableData();

    const { colors, unsetColors, addColors } = useByoColors(tableData, sortingKey);

    const pairedFlavorsData = usePairedFlavors(activeKeywordName);

    const keywordItems = useMemo(
        () => tableData
            .filter(i => !i.isLoading)
            .map((item) => {
                return {
                    ...item.keyword,
                    isActive: activeKeywordName !== null && equalInLC(item.keyword.name ?? '', activeKeywordName ?? ''),
                    color: colors.get(item.keyword.name ?? '') ?? '',
                    chartData: item.chartData,
                };
            }),
        [activeKeywordName, tableData, colors]
    );

    const { chartData, barChartData } = useByoChartData(keywordItems);

    const applySearch = useCallback(
        (lastQuery: string = '') => {
            const finalSearch = lastQuery.trim() || search.trim();
            const addedWords = addWordsFromQuery(finalSearch);

            addColors(addedWords.map(word => word.keyword.name ?? ''));

            setSearch('');
            if (addedWords.length === 0) {
                setIsSearchError(true);
            } else {
                resetSearch();
            }
        },
        [search, addWordsFromQuery, addColors, resetSearch]
    );

    const isAllLoading = useMemo(
        () => data.length > 0 && data.every(w => w.isLoading),
        [data]
    );

    const isEmpty = data.length === 0;

    useEffect(() => {
        if (search.trim().length > 0) {
            setIsSearchError(false);
        }
    }, [search]);

    const tableRows = useMemo(
        () => tableData.map(i => ({
            ...i.keyword,
            name: i.keyword.name ?? '',
            color: colors.get(i.keyword.name ?? '') ?? '',
            isLoading: i.isLoading,
            isSelected: !!activeKeywordName && equalInLC(i.keyword.name ?? '', activeKeywordName ?? ''),
            columnsValues: i.columnsValues,
            chartData: i.chartData.actualData,
        })),
        [tableData, activeKeywordName, colors]
    );

    const filteredKeywords = useMemo(
        (clickedKeywordName?: string) => {
            const trimmedSearch = (clickedKeywordName ?? search).trim();
            if (trimmedSearch.length < 3) {
                return [];
            }
            return [
                ...keywords.filter(
                    i => startsFromInLC(i.name ?? '', trimmedSearch)
                ),
                ...keywords.filter(
                    i => !startsFromInLC(i.name ?? '', trimmedSearch)
                        && includesInLC(i.name ?? '', trimmedSearch)
                ),
            ];
        },
        [keywords, search]
    );

    const tableSortingContextValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                ...columns.map(col => ({
                    id: col.sortKey,
                    name: col.sortKey,
                })),
                {
                    id: 'macStage',
                    name: 'macStage',
                    defaultDirection: Sort.Asc,
                },
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as string);
                if (direction) {
                    setSortingDirection(direction);
                }
            },
        }),
        [sortingKey, sortingDirection, columns, setSortingKey, setSortingDirection]
    );

    const selectKeyword = useCallback(
        (keywordName?: string | null) => {
            setActiveKeywordName(keywordName ?? null);
        },
        []
    );

    const toggleColorSelection = useCallback(
        (keywordName: string) => {
            if (colors.has(keywordName)) {
                unsetColors([keywordName]);
            } else {
                addColors([keywordName]);
            }
        },
        [colors, addColors, unsetColors]
    );

    const baseTableConfig = useByoTableConfig({
        rows: tableRows,
        columns,
        activeAnalyticsType,
    });

    const handleCopyTable = useCallback(() => {
        const extractedDate = `Extracted ${getFormattedDate(new Date().toISOString(), 'MMMM D, YYYY')}`;

        const baseData = getCopyTableData(baseTableConfig as TableColumn[], tableData.length);
        const footerData = baseTableConfig.flatMap(({ footerText }) => footerText || '');

        const copyData = [
            [extractedDate],
            [SUPPORT_CONTACT_INFO],
            ...baseData,
            footerData,
        ];

        const copyText = formatCopyDataAsString(copyData);

        copyToClipboard(copyText);

        const isSuccess = copyText.split('\t').filter(e => e.trim()).length > 1;

        if (isSuccess) {
            toast.success("Successfully copied to your clipboard");
        } else {
            toast.error("Something went wrong");
        }
    }, [baseTableConfig, tableData.length]);

    const title = useMemo(
        () => {
            if (byoSettingName) {
                return `Build Your Own Chart - ${byoSettingName}`;
            }
            else if (instantChartName) {
                return `Build Your Own Chart - ${instantChartName}`;
            }
            else {
                return 'Build Your Own Chart';
            }
        },
        [byoSettingName, instantChartName]
    );

    return (
        <>
            <div className="ByoPage__content">
                <div className="ByoPage__title">
                    {title}
                </div>
                {!isEmpty && (
                    <div className="ByoPage__header appears-top del-1">
                        <div className="ByoPage__header-left">
                            <AnalyticsTypeTabs/>
                        </div>
                        <div className="ByoPage__header-right">
                            <SelectFilters testId="buttonByoPageFilters"/>
                        </div>
                    </div>
                )}
                {!isEmpty && hasCharts && (
                    <div className="ByoPage__top-panel">
                        <div className="ByoPage__top-panel-chart appears-top del-2">
                            <ChartPanel
                                chartData={chartData}
                                barChartData={barChartData}
                                isLoading={isAllLoading}
                            />
                        </div>
                        <div className="ByoPage__top-panel-paired-flavors appears-top del-2">
                            <ProgressPanel data={pairedFlavorsData} isLoading={isAllLoading}/>
                        </div>
                    </div>
                )}
                <div className="ByoPage__row appears-top del-3 z-2">
                    <SearchField
                        searchQuery={search}
                        searchError={isSearchError}
                        setSearchQuery={setSearch}
                        onEnter={applySearch}
                        autoCompleteData={filteredKeywords}
                        isDisabled={isAllLoading}
                        modifiers={["static"]}
                        isUpperCase
                        isMultipleFromBufferAllowed
                        tooltipId="byo-search-tooltip"
                    />
                    {tableData.length === 0 && (
                        <Tooltip
                            id="byo-search-tooltip"
                            place="right"
                            isOpen
                        >
                            Build your own chart by<br/>searching for a food or flavor
                        </Tooltip>
                    )}
                    {slices.length > 0 && <SlicesTabs slices={slices}/>}
                    {tableData.length > 0 && (
                        <button
                            className="ByoPage__copy-btn"
                            onClick={handleCopyTable}
                        >
                            COPY TABLE
                        </button>
                    )}
                </div>
                <div className="appears-top del-4">
                    {tableData.length > 0 && (
                        <TableSortingContext.Provider value={tableSortingContextValue}>
                            <ByoTable
                                rows={tableRows}
                                columns={columns}
                                isLoading={isAllLoading}
                                showConsumerPermissionOverlay={consumerAccessRestricted}
                                selectKeyword={selectKeyword}
                                toggleColorSelection={toggleColorSelection}
                            />
                        </TableSortingContext.Provider>
                    )}
                </div>
            </div>

            <div className="ByoPage__footer appears-top del-5">
                <FooterContent/>
            </div>
        </>
    );
};

const Byo: FC = () => {
    const {
        isLoading: isAiSearchSettingsLoading,
        isActive: isAiSearchActive,
    } = useAiSearchSettingsContext();
    const [wordsString] = useQueryState(BYO_WORDS_QUERY_PARAM_NAME, '');
    const [instantChartIdString] = useQueryState(BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME);
    const [byoSettingsIdString] = useQueryState(BYO_SETTINGS_ID_QUERY_PARAM_NAME);
    const aiSearchSettings = useAiSearchByoSettings();

    return (
        <Page
            title="Build Your Own"
            isWrapped
            key={`${wordsString}-${instantChartIdString}-${byoSettingsIdString}`}
        >
            <div className="ByoPage">
                <CSSTransition
                    in={isAiSearchActive}
                    classNames="appears"
                    timeout={600}
                    unmountOnExit
                >
                    <AiResultSection
                        settings={aiSearchSettings}
                        isSettingsLoading={isAiSearchSettingsLoading}
                    />
                </CSSTransition>
                {!isAiSearchSettingsLoading && (
                    <ByoDataContextProvider>
                        <ByoPageContent/>
                    </ByoDataContextProvider>
                )}
            </div>
        </Page>
    );
};

export default Byo;